import type {
    CloudPaymentsCompleteHandler,
    CloudPaymentsPopupParams,
    ConstructableCloudPaymentsWidget,
    PaymentType,
} from '@interfaces/CloudPayments';
import type ValueList from '@interfaces/ValueList';
import Analytics from '@services/analytics/Analytics';
import PopupBuilder from '@services/payment/PopupBuilder';
import type {RecurrentParams} from '@interfaces/PaymentPopup';
import {ProductType} from '@constants/productType';

declare global {
    interface Window {
        cp: {
            CloudPayments: ConstructableCloudPaymentsWidget;
        };
    }
}

export default class CloudPaymentsPopup extends PopupBuilder {
    private readonly params: CloudPaymentsPopupParams;
    private onComplete?: CloudPaymentsCompleteHandler;

    public constructor(email: string, description: string, product: string) {
        super();
        this.params = {
            publicId: this.getPublicId(product),
            description,
            amount: 0,
            currency: 'RUB',
            accountId: email,
            email,
            requireEmail: true,
            data: {
                cloudPayments: {
                    customerReceipt: {
                        Items: [],
                        email,
                    },
                },
            },
        };
    }

    private getPublicId(product: string) {
        switch (product) {
            case ProductType.Club:
            case ProductType.Payment:
                return window.bootstrapParams.agencyCloudPaymentsPublicKey;
            default:
                throw new Error(`Unknown product: ${product}`);
        }
    }

    public setRecurrentData(params: RecurrentParams): this {
        this.params.data.cloudPayments.recurrent = {
            interval: 'Month',
            period: params.monthNum,
            amount: params.amount ?? this.params.amount,
            ...(params.startDate && {startDate: params.startDate}),
            customerReceipt: this.params.data.cloudPayments.customerReceipt,
        };
        return this;
    }

    public setCustomData(params: ValueList): this {
        this.params.data.paperpaper = params;
        return this;
    }

    public setItem(description: string, price: number): this {
        const item = {
            label: description,
            price,
            quantity: 1.0,
            amount: price,
            vat: null,
        };
        this.params.data.cloudPayments.customerReceipt.Items.push(item);

        if (this.params.data.cloudPayments.recurrent) {
            this.params.data.cloudPayments.recurrent.customerReceipt.Items.push(item);
        }
        this.params.amount += price;
        return this;
    }

    public setCompleteHandler(handler: CloudPaymentsCompleteHandler): this {
        this.onComplete = handler;
        return this;
    }

    public async buildCharge() {
        return this.build('charge');
    }

    public async buildAuth() {
        return this.build('auth');
    }

    public async build(type: PaymentType) {
        const widget = new window.cp.CloudPayments({startWithButton: false});
        return new Promise((resolve, reject) => {
            widget.pay(type, this.params, {
                onSuccess: options => {
                    resolve(options);
                },
                onFail: reason => {
                    Analytics.triggerEvent(
                        `Закрытие виджета оплаты без покупки${
                            type === 'auth' ? ' (двухфакторный)' : ''
                        }`,
                        'widgetReject',
                        [reason],
                    );
                    reject(Error(reason));
                },
                onComplete: this.onComplete,
            });
        });
    }
}
